import {
  AspectRatioPlaceholder,
  Button,
  Dialog,
  ImagePreloader,
  MediaDialog,
  MediaDialogMedia,
  Notification,
  Spinner,
} from "@rio-cloud/rio-uikit";
import { CheckpointStep, CheckpointStop } from "@types";
import { formatTimeFromMinutes, formatCheckpointStopDateTime, formatChecklistTitle, parseCheckpointImageSources } from "@utils";
import { useState, useRef, useEffect } from "react";
import { getTripCheckpointDocuments, TripCheckpointDocument } from "src/services/trips";
import { getStepDocumentFile } from "src/services/checkpoint";
import { triggerDownload } from "src/utils/documents";
import DocumentCard from "src/components/DocumentCard";

interface Props {
  open: boolean;
  onClose?: () => void;
  stage?: CheckpointStep;
  totalStages: number;
  tripId?: string;
  checkpointId?: string;
  checkpointStop?: CheckpointStop;
}

const fallback = (
  <AspectRatioPlaceholder width={64} height={64}>
    <div className="text-size-h3 display-grid place-items-center inset-0">
      <span className="rioglyph rioglyph-missing text-size-60 text-color-gray" />
    </div>
  </AspectRatioPlaceholder>
);

const Image = ({ src, onClick }: { src: string; onClick: () => void }) => {
  return (
    <ImagePreloader src={src}>
      {({ status, image }) => {
        switch (status) {
          case "FAILED":
            return fallback;

          case "LOADED":
            return (
              <div className="cursor-pointer width-200 height-150 rounded overflow-hidden" onClick={onClick}>
                <img
                  src={image.src}
                  className="img-responsive ani-fade-in"
                  alt="Foto do checklist"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            );

          default:
            return (
              <AspectRatioPlaceholder width={64} height={64} className="width-60 height-60">
                <Spinner isInverse={false} />
              </AspectRatioPlaceholder>
            );
        }
      }}
    </ImagePreloader>
  );
};

const DialogCarousel = ({ open, onClose, images }: { open: boolean; onClose: () => void; images: string[] }) => {
  const dataImage = images
    .filter((src) => src)
    .map((src, index) => ({
      src,
      type: MediaDialog.MEDIA_TYPE_IMAGE,
      title: `Foto ${index + 1}`,
    }));

  if (dataImage.length === 0) return null;

  return (
    <MediaDialog
      show={open}
      onClose={onClose}
      showCloseButton
      previousButtonText="Anterior"
      useOverflow={false}
      nextButtonText="Próximo"
      bsSize="fullscreen"
      media={dataImage as MediaDialogMedia[]}
    />
  );
};

const Checkpoint = ({ open, onClose, stage, totalStages, tripId, checkpointId, checkpointStop }: Props) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [carouselImages, setCarouselImages] = useState<string[]>([]);
  const [docs, setDocs] = useState<TripCheckpointDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<TripCheckpointDocument | null>(null);
  const fetchedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!open || !tripId) return;

    const key = `${tripId}::${checkpointId ?? "all"}`;
    if (fetchedRef.current.has(key)) return;
    fetchedRef.current.add(key);

    setLoadingDocs(true);
    getTripCheckpointDocuments(tripId)
      .then((result) => {
        const filtered = checkpointId ? result.filter((d) => d.checkpointId === checkpointId) : result;
        setDocs(filtered);
      })
      .catch(() => {
        fetchedRef.current.delete(key);
        setDocs([]);
      })
      .finally(() => setLoadingDocs(false));
  }, [open, tripId, checkpointId]);

  useEffect(() => {
    if (!open) {
      setDocs([]);
      setViewingDoc(null);
      setSelectedPhotoIndex(null);
      setCarouselImages([]);
      fetchedRef.current.clear();
    }
  }, [open]);

  const handleDownloadDoc = async (doc: TripCheckpointDocument) => {
    const fileName = doc.originalName ?? doc.fileName;
    const canUseStepApi = doc.checkpointId && doc.stepId && doc.fieldId && doc.fileName;

    if (canUseStepApi) {
      try {
        const blob = await getStepDocumentFile(doc.checkpointId, doc.stepId, doc.fieldId, doc.fileName);
        const objectUrl = URL.createObjectURL(blob);
        triggerDownload(objectUrl, fileName);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
      } catch {
        Notification.error(`Erro ao baixar "${fileName}".`);
      }
      return;
    }

    if (doc.downloadUrl) {
      window.open(doc.downloadUrl, "_blank", "noopener,noreferrer");
      return;
    }

    Notification.error(`Não foi possível baixar "${fileName}".`);
  };

  const getFieldUi = (title: string, estimatedValue: string, actualValue: string) => {
    return (
      <div className="width-100pct width-350-md">
        <div>
          <label>
            {title || ""} {estimatedValue ? `(Estimado ${estimatedValue})` : ""}
          </label>
          <input type="text" className="form-control" id="inputEmail" placeholder="" value={actualValue} disabled />
        </div>
      </div>
    );
  };

  return (
    <Dialog
      show={open}
      title={<span className="text-bold text-size-18">{`Etapa ${stage?.index}/${totalStages} - ${stage?.title}`}</span>}
      onClose={onClose}
      bodyClassName="padding-25"
      footer={
        <Button className="width-100" onClick={onClose}>
          Voltar
        </Button>
      }
      body={
        <div className="display-flex flex-column gap-25">
          <div className="display-flex flex-column gap-5">
            <span className="text-bold text-size-16">{formatChecklistTitle(checkpointStop?.checkpointType)}</span>
          </div>

          <div className="display-flex">
            <div>
              <span>Tempo Estimado: </span>
              <span>
                <span className="text-normal text-size-14 rioglyph rioglyph-time text-color-gray" />
                <span className="text-normal text-size-14 margin-left-5">
                  {formatTimeFromMinutes(stage?.estimatedTimeMinutes)}
                </span>
              </span>
            </div>
            <div className="margin-x-20">
              <span>|</span>
            </div>
            <div>
              <span>Tempo Realizado: </span>
              <span>
                <span className="text-normal text-size-14 rioglyph rioglyph-time text-color-gray" />
                <span className="text-normal text-size-14 margin-left-5">{formatTimeFromMinutes(stage?.realizedTimeMinutes)}</span>
              </span>
            </div>
          </div>

          <div className="display-flex flex-column gap-2">
            <span className="text-size-14 text-color-dark">
              <strong>Data e hora da parada: </strong>
              {formatCheckpointStopDateTime(checkpointStop)}
            </span>
            <span className="text-size-14 text-color-dark">
              <strong>Local: </strong>
              {checkpointStop?.address || "---"}
            </span>
          </div>

          <div className="">
            <form>
              <fieldset className="display-flex justify-content-between flex-row flex-wrap">
                {stage?.fields?.map((step) => {
                  const type = step?.field?.type;
                  const title = step?.field?.title;
                  const estimatedValue = step?.estimatedValue || "";
                  const actualValue = step?.actualValue;

                  if (type === "IMAGE") return "";

                  if (step?.field?.name === "OBSERVATION") return "";

                  if (step?.field?.name === "DOCUMENT") return "";

                  if (type === "BOOLEAN") return getFieldUi(title, estimatedValue, actualValue ? "Sim" : "Não");

                  return getFieldUi(title, estimatedValue, actualValue || "");
                })}
              </fieldset>
            </form>
          </div>

          {stage?.fields?.map((step) => {
            if (step?.field?.name !== "OBSERVATION") return "";

            return (
              <div key={step.id}>
                <form>
                  <fieldset>
                    <div className="form-group padding-0">
                      <label form="textArea">{step?.field?.title}</label>
                      <textarea
                        disabled
                        className="form-control"
                        rows={3}
                        id="textArea"
                        value={step?.actualValue?.toString() || ""}
                      />
                    </div>
                  </fieldset>
                </form>
              </div>
            );
          })}

          {stage?.fields?.map((step) => {
            const isDocument = step?.field?.name === "DOCUMENT";
            const isImage = !isDocument && step?.field?.type === "IMAGE";
            if (!isImage && !isDocument) return null;

            if (isDocument) {
              return (
                <div key={step.id}>
                  <span className="text-color-dark text-normal text-size-12">{step?.field?.title}</span>
                  {loadingDocs ? (
                    <div className="display-flex align-items-center gap-5 margin-top-4">
                      <Spinner />
                      <span className="text-size-12">Carregando...</span>
                    </div>
                  ) : docs.length === 0 ? (
                    <span className="text-size-12 text-color-gray margin-top-4 display-block">Nenhum arquivo</span>
                  ) : (
                    <div className="display-flex flex-row gap-10 margin-top-4 flex-wrap">
                      {docs.map((doc, i) => (
                        <DocumentCard
                          key={i}
                          displayName={doc.originalName ?? doc.fileName}
                          downloadUrl={doc.downloadUrl}
                          mimeType={doc.mimeType}
                          onDownload={() => handleDownloadDoc(doc)}
                          onPreview={
                            doc.mimeType?.startsWith("image/") && doc.downloadUrl
                              ? () => setViewingDoc(doc)
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            const images = parseCheckpointImageSources(step?.actualValue, step?.actualValues);
            if (images.length === 0) return null;

            return (
              <div key={step.id}>
                <span className="text-color-dark text-normal text-size-12">{step?.field?.title}</span>
                <div className="display-flex gap-10 margin-top-4 flex-wrap">
                  {images.map((src: string, i: number) => (
                    <Image
                      key={i}
                      src={src}
                      onClick={() => {
                        setCarouselImages(images);
                        setSelectedPhotoIndex(i);
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {(() => {
            const hasDocumentField = stage?.fields?.some((step) => step?.field?.name === "DOCUMENT");
            if (hasDocumentField) return null;

            if (docs.length === 0 && !loadingDocs) return null;

            return (
              <div key="extra-docs" className="margin-top-15">
                <span className="text-color-dark text-normal text-size-12">Documentos</span>
                {loadingDocs ? (
                  <div className="display-flex align-items-center gap-5 margin-top-4">
                    <Spinner />
                    <span className="text-size-12">Carregando...</span>
                  </div>
                ) : (
                  <div className="display-flex flex-row gap-10 margin-top-4 flex-wrap">
                    {docs.map((doc, i) => (
                      <DocumentCard
                        key={`extra-doc-${i}`}
                        displayName={doc.originalName ?? doc.fileName}
                        downloadUrl={doc.downloadUrl}
                        mimeType={doc.mimeType}
                        onDownload={() => handleDownloadDoc(doc)}
                        onPreview={
                          doc.mimeType?.startsWith("image/") && doc.downloadUrl
                            ? () => setViewingDoc(doc)
                            : undefined
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          <DialogCarousel
            open={selectedPhotoIndex !== null}
            images={carouselImages}
            onClose={() => {
              setSelectedPhotoIndex(null);
              setCarouselImages([]);
            }}
          />

          <Dialog
            show={!!viewingDoc}
            onClose={() => setViewingDoc(null)}
            onEsc={() => setViewingDoc(null)}
            bsSize="lg"
            title={viewingDoc?.originalName ?? viewingDoc?.fileName ?? ""}
            body={
              viewingDoc ? (
                <div className="display-flex flex-column align-items-center gap-10">
                  <img
                    src={viewingDoc.downloadUrl}
                    alt={viewingDoc.originalName ?? viewingDoc.fileName}
                    style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
                  />
                  <Button
                    iconName="rioglyph rioglyph-download"
                    onClick={() => handleDownloadDoc(viewingDoc)}
                  >
                    Baixar
                  </Button>
                </div>
              ) : null
            }
          />
        </div>
      }
      className=""
      showCloseButton
      bsSize="md"
    />
  );
};

export default Checkpoint;
